/**
 * 客服记录
 */
import dayjs from 'dayjs';
import React, { useEffect, useState } from "react";
import { Button, Drawer, Timeline, Divider, Popconfirm, message, Input, Modal, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { serviceRecoedRequest, deleteServiceRecoed, serviceAndWorkOrder, deleteServiceAndWorkOrder, closeServiceAndWorkOrder, getCustomerRecordRequest, insertCustomerRecordRequest } from '@/service/carDiagnosis';
import { batchAssignServiceTask, addContinueServiceTask } from '@/service/serviceTask';
import { userListRequest } from '@/service/userManage';
import PermissionButton from '@/components/permissionButton';
import LoadingWrapper from '@/components/loadingWrapper';
import OperateService from '../operateService';
import OperateModal from '../../../workOrder/components/operateModal';
import WorkOrderDetail from '../../../workOrder/components/workOrderDetail';
import { deleteWorkOrderRequest, workOrderDetailRequest } from '@/service/workOrder';
import { getInnerPermissionEnable } from '@/utils/permission';
import ServiceItem from '../serviceItem';
import styles from './index.module.scss';

interface MaintenanceRecordProps {
  carInfo: any; // 车辆信息
  onClose: () => void; // 关闭客服记录弹层的方法
  showInterventionFlag?: boolean; // 是否显示续管车客服介入（仅车辆诊断页面传入）
  onInterventionSuccess?: () => void; // 介入成功后的回调
}
const ServiceRecoed = (props: MaintenanceRecordProps) => {
  const { carInfo, carInfo: { plate_number, id, frame_number }, onClose, showInterventionFlag, onInterventionSuccess } = props;
  const [page, setPage] = useState(1); // 页码
  const [totalCount, setTotalCount] = useState(0); // 总数
  const [recordData, setRecordData] = useState<any>([]); // 客服记录数据
  const [loading, setLoading] = useState<boolean>(false) // 客服记录数据是否在请求中
  const [operateVisible, setOperateVisible] = useState<any>({}) // 添加客服记录的弹窗
  const [addWorkOrder, setAddWorkOrder] = useState<any>({}); // 创建工单的弹窗
  const [operateInfo, setOperateInfo] = useState<any>({}); // 查看工单的弹窗
  const [remark, setRemark] = useState<string>(''); // 特殊情况说明
  const [classify, setClassify] = useState<string>(''); // 沟通类型
  const [userData, setUserData] = useState<any>([]); // 客服人员数据
  const [distributionVisible, setDistributionVisible] = useState<boolean>(false); // 派发客服工单弹窗
  const [distributionLoading, setDistributionLoading] = useState<boolean>(false); // 派发提交loading
  const [distributionCustomerId, setDistributionCustomerId] = useState<number | undefined>(undefined); // 选中的客服人员id
  const [currentDistributionId, setCurrentDistributionId] = useState<number | undefined>(undefined); // 当前派发记录id
  const [interventionFlag, setInterventionFlag] = useState<string>(carInfo?.interventionFlag || '否'); // 续管车客服是否介入
  const [interventionLoading, setInterventionLoading] = useState<boolean>(false); // 介入按钮loading
  // 是否有添加客服记录的权限
  const hasServiceRecoedAddPermission = getInnerPermissionEnable('/car-admin/car-diagnosis/service-recoed-add');

  // 获取特殊情况说明
  useEffect(() => {
    if (id) {
      getCustomerRecordRequest(id).then(res => {
        if (res.data?.code === 0) {
          setRemark(res.data.data || '');
        }
      });
    }
  }, [id]);

  // 保存特殊情况说明
  const handleSaveRemark = async () => {
    try {
      await insertCustomerRecordRequest({
        carId: id,
        content: remark
      });
      message.success('特殊情况说明已保存');
    } catch (error) {
      console.error(error);
    }
  }

  // 获取客服记录
  const getMaintenancesRecord = async (newPage: number, currentClassify = classify) => {
    try {
      setLoading(true);
      const params: any = { frameNumber: frame_number, page: newPage, limit: 10 };
      if (currentClassify) {
        params.classify = currentClassify;
      }
      const res = await serviceRecoedRequest(params);
      const data = res.data?.data || [];
      if (newPage === 1) {
        setRecordData(data);
      } else {
        setRecordData([...recordData, ...data]);
      }
      setPage(newPage);
      setLoading(false);
      setTotalCount(res.data?.count || 0);
    } catch (error) {
      setLoading(false);
    }
  }

  useEffect(() => {
    getMaintenancesRecord(1);
    getUserData();
  }, []);

  // 获取客服人员数据
  const getUserData = async () => {
    const res = await userListRequest({ page: 1, limit: 9999 });
    setUserData(res.data?.data || []);
  }

  // 加载更多
  const loadMore = () => {
    getMaintenancesRecord(page + 1);
  }

  // 筛选切换
  const handleFilterChange = (value: string) => {
    setClassify(value);
    getMaintenancesRecord(1, value);
  }

  // 删除客服记录
  const deleteRecord = async (id: number) => {
    const res = await deleteServiceRecoed(id);
    if (res.data?.code === 0) {
      message.success('删除成功');
      getMaintenancesRecord(1);
    }
  }

  // 查看工单
  const examineWorkOrder = async (orderId: any) => {
    const res = await workOrderDetailRequest(orderId);
    const data = res.data?.data || {};
    setOperateInfo({ type: 'detail', data: data })
  }

  // 撤销工单
  const deleteWorkOrder = async (value: any) => {
    const params = { orderId: value.orderId, customerId: value.id }
    const res = await deleteServiceAndWorkOrder(params)
    if (res?.data?.code === 0) {
      const res = await deleteWorkOrderRequest(value.orderId);
      if (res?.data?.code === 0) {
        message.success('撤销成功');
        getMaintenancesRecord(1);
      }
    }
  }

  // 结束处理
  const closeWorkOrder = async (value: any) => {
    const params = { customerId: value.id }
    const res = await closeServiceAndWorkOrder(params)
    if (res?.data?.code === 0) {
      message.success('已结束处理');
      getMaintenancesRecord(1);
    }
  }

  // 插入客服记录和工单的关系
  const getWorkOrderId = async (orderId: any, customerId: any) => {
    const params = { orderId: orderId, customerId: customerId }
    const res = await serviceAndWorkOrder(params)
    getMaintenancesRecord(1);
  }

  const anyData = () => {

  }

  // 打开派发客服工单弹窗
  const openDistributionModal = (recordId: number) => {
    setCurrentDistributionId(recordId);
    setDistributionCustomerId(undefined);
    setDistributionVisible(true);
  }

  // 提交派发客服工单
  const handleDistributionSubmit = async () => {
    if (!distributionCustomerId) {
      message.warning('请选择客服人员');
      return;
    }
    if (!currentDistributionId) {
      message.warning('未获取到当前记录ID');
      return;
    }
    setDistributionLoading(true);
    try {
      const res = await batchAssignServiceTask({
        assignCustomerId: distributionCustomerId,
        ids: [currentDistributionId],
      });
      if (res.data?.code === 0) {
        message.success('派发成功');
        setDistributionVisible(false);
        getMaintenancesRecord(1);
      }
    } finally {
      setDistributionLoading(false);
    }
  }

  // 续管车客服介入
  const handleIntervention = async () => {
    setInterventionLoading(true);
    try {
      const res = await addContinueServiceTask({ carId: id });
      if (res.data?.code === 0) {
        message.success('介入成功');
        setInterventionFlag('是');
        onInterventionSuccess?.();
      }
    } finally {
      setInterventionLoading(false);
    }
  }

  const filterOptions = [
    { label: "全部", value: "" },
    { label: "续费沟通", value: "续费沟通" },
    { label: "事故沟通", value: "事故沟通" },
    { label: "减/免责情况沟通", value: "减/免责情况沟通" },
    { label: "日常沟通", value: "日常沟通" },
  ];

  return (
    <Drawer
      title={`${plate_number}客服记录`}
      visible
      onClose={onClose}
      className={styles.recordDrawer}
      size="large"
      bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: 0 }}
    >
      {showInterventionFlag && (
        <div style={{ paddingBottom: '10px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold', marginRight: 8 }}>续管车客服是否介入：</span>
          <span style={{ marginRight: 24 }}>{interventionFlag || '否'}</span>
          {interventionFlag !== '是' && (
            <Popconfirm
              title="确认要进行客服介入吗？"
              onConfirm={handleIntervention}
              okText="确认"
              cancelText="取消"
            >
              <Button type="primary" size="small" loading={interventionLoading}>去介入</Button>
            </Popconfirm>
          )}
        </div>
      )}
      {hasServiceRecoedAddPermission && (
        <div style={{ paddingBottom: '10px', flexShrink: 0 }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>特殊情况说明：</span>
            <Button type="primary" size="small" onClick={handleSaveRemark}>保存</Button>
          </div>
          <Input.TextArea
            rows={3}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="请输入特殊情况说明"
            allowClear
          />
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <PermissionButton
            type="primary"
            operateType='/car-admin/car-diagnosis/service-recoed-add'
            onClick={() => setOperateVisible({ show: true, type: 'carDiagnosisAdd' })}
          >
            新增客服记录
          </PermissionButton>
          <Select
            showSearch
            allowClear
            placeholder="请选择沟通类型"
            options={filterOptions}
            value={classify}
            onChange={handleFilterChange}
            style={{ width: 200, marginLeft: 8 }}
            optionFilterProp="label"
          />
        </div>
        <LoadingWrapper spinning={loading}>
          <Timeline>
            {recordData.map((item: any, index: any) => {
              return (
                <Timeline.Item key={index}>
                  <div>
                    {item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss') : item.createdAt}
                    <Popconfirm
                      title="此操作将删除该条客服记录, 是否继续?"
                      onConfirm={() => deleteRecord(item.id)}
                    >
                      <PermissionButton
                        operateType='/car-admin/car-diagnosis/service-recoed-delete'
                        type="link"
                        style={{ padding: '0 0 0 6px', height: '26px' }}
                      >
                        <DeleteOutlined style={{ color: 'red' }} />
                      </PermissionButton>
                    </Popconfirm>
                  </div>
                  <ServiceItem column={{ xs: 1, sm: 3 }} data={item} type="maintenance" />
                  <div className={styles.fourButton}>
                    <div>
                      {item.state == 1 ? <PermissionButton
                        operateType='/car-admin/car-diagnosis/service-recoed-edit'
                        onClick={() => setOperateVisible({ show: true, type: 'edit', data: item })}
                        block={true}
                      >编辑</PermissionButton> : item.state == 2 ? <PermissionButton
                        operateType='/car-admin/car-diagnosis/service-recoed-edit'
                        onClick={() => setOperateVisible({ show: true, type: 'edit', data: item })}
                        block={true}
                      >编辑</PermissionButton> : <div></div>}
                    </div>
                    <div>
                      {item.state == 1 ? <PermissionButton
                        operateType='/car-admin/car-diagnosis/service-recoed-work-order-add'
                        onClick={() => setAddWorkOrder({ type: 'servicrAdd', data: item })}
                        block={true}
                      >创建工单</PermissionButton> : item.state == 2 ? <PermissionButton
                        operateType='/car-admin/car-diagnosis/service-recoed-work-order-examine'
                        onClick={() => examineWorkOrder(item.orderId)}
                        block={true}
                      >查看工单</PermissionButton> : item.state == 3 ? <PermissionButton
                        operateType='/car-admin/car-diagnosis/service-recoed-work-order-examine'
                        onClick={() => examineWorkOrder(item.orderId)}
                        block={true}
                      >查看工单</PermissionButton> : <div></div>}
                    </div>
                    <div>
                      {item.state == 1 ? <Popconfirm
                        title="是否结束处理此工单?"
                        onConfirm={() => closeWorkOrder(item)}
                      >
                        <PermissionButton
                          operateType='/car-admin/car-diagnosis/service-recoed-work-order-delete'
                          block={true}
                        >结束处理</PermissionButton>
                      </Popconfirm> : item.state == 2 ? <Popconfirm
                        title="是否撤销此工单?"
                        onConfirm={() => deleteWorkOrder(item)}
                      >
                        <PermissionButton
                          operateType='/car-admin/car-diagnosis/service-recoed-work-order-delete'
                          block={true}
                        >撤销工单</PermissionButton>
                      </Popconfirm> : <div></div>}
                    </div>
                    <div>
                      <PermissionButton
                        operateType='/car-admin/car-diagnosis/service-recoed-distribution'
                        onClick={() => openDistributionModal(item.id)}
                        block={true}
                      >派发客服工单</PermissionButton>
                    </div>
                  </div>
                </Timeline.Item>
              )
            })}
          </Timeline>
          {recordData.length ?
            <Divider plain>
              {totalCount > recordData.length ?
                <Button type="primary" onClick={loadMore}>加载更多</Button> :
                "已加载全部数据"
              }
            </Divider> :
            <Divider plain>暂无客服记录</Divider>
          }
        </LoadingWrapper>
      </div>

      {/* 添加客服记录的弹窗 */}
      {operateVisible.show &&
        <OperateService
          type={operateVisible.type}
          onClose={() => setOperateVisible({ show: false, type: 'carDiagnosisAdd' })}
          reloadData={() => getMaintenancesRecord(1)}
          carId={carInfo}
          anyData={anyData}
          editData={operateVisible.data}
        />
      }
      {/* 创建工单的弹窗 */}
      {['add', 'edit', 'servicrAdd'].includes(addWorkOrder?.type) &&
        <OperateModal
          type={addWorkOrder.type}
          workOrderInfo={addWorkOrder.data}
          onClose={() => setAddWorkOrder({})}
          reloadData={() => getMaintenancesRecord(1)}
          workId={getWorkOrderId}
        />
      }
      {/* 查看工单的弹窗 */}
      {operateInfo?.type === 'detail' &&
        <WorkOrderDetail item={operateInfo.data} onClose={() => setOperateInfo({})} />
      }
      {/* 派发客服工单弹窗 */}
      <Modal
        title="派发客服工单"
        visible={distributionVisible}
        onOk={handleDistributionSubmit}
        onCancel={() => setDistributionVisible(false)}
        confirmLoading={distributionLoading}
        destroyOnClose
      >
        <div style={{ padding: '20px 0' }}>
          <span style={{ marginRight: 8 }}>客服人员：</span>
          <Select
            placeholder="请选择客服"
            options={userData}
            allowClear
            showSearch
            fieldNames={{
              label: 'nickname',
              value: 'id'
            }}
            optionFilterProp="nickname"
            style={{ width: 300 }}
            value={distributionCustomerId}
            onChange={(val) => setDistributionCustomerId(val)}
          />
        </div>
      </Modal>
    </Drawer>
  )
}

export default ServiceRecoed;